/**
 * A calendar date in and out as ISO `YYYY-MM-DD`, or "" when there is none.
 *
 * `date` is the browser's own date control in the `Input` box, for a date a person picks near today.
 * `birthDate` is three numeric fields, month, day and year, because scrolling a picker back
 * decades on a phone is the slowest way to say a birthday. It tells the phone to show the number
 * pad and the browser what it can fill in. Until the three parts make a real date in the past the
 * value out is "" and, once all three are filled, the fields say what is wrong. Put either inside a
 * `Field` for its label.
 */
import * as stylex from "@stylexjs/stylex";
import { useId, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { border, space } from "@lattice/tokens/space.stylex";
import { controlStyle } from "./controlStyle.ts";
import { focusRing } from "./focus.ts";
import { WarningIcon } from "./icons.tsx";
import { Input } from "./Input.tsx";
import { textStyle } from "./textStyle.ts";
const styles = {
  group: {
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kOIVth: "x1173whf",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  part: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x15xv0s5",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  month: {
    kzqmXN: "xex909m",
    $$css: true
  },
  day: {
    kzqmXN: "xex909m",
    $$css: true
  },
  year: {
    kzqmXN: "x1l24a0r",
    $$css: true
  },
  caption: {
    kMwMTN: "x1lm7cez",
    $$css: true
  },
  error: {
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kOIVth: "xskm3c9",
    kogj98: "x1ghz6dp",
    kMwMTN: "x6ygkrl",
    $$css: true
  },
  column: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "xskm3c9",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  number: {
    k9WMMc: "x2b8uid",
    $$css: true
  },
  wrongPart: {
    kMzoRj: "x4lnxqg",
    $$css: true
  }
};
const FIRST_YEAR = 1900;

/** The ISO date for these parts, or null when they are not a real date on or before `today`. */
export function isoFromParts(year: string, month: string, day: string, today: string = new Date().toISOString().slice(0, 10)): string | null {
  if (!/^\d{4}$/.test(year) || !/^\d{1,2}$/.test(month) || !/^\d{1,2}$/.test(day)) return null;
  const [y, m, d] = [Number(year), Number(month), Number(day)];
  const made = new Date(Date.UTC(y, m - 1, d));
  const real = y >= FIRST_YEAR && made.getUTCFullYear() === y && made.getUTCMonth() === m - 1 && made.getUTCDate() === d;
  if (!real) return null;
  const iso = `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  return iso <= today ? iso : null;
}
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;

/** What is wrong with one part of the date, and the part to mark. */
interface Problem {
  readonly part: "month" | "day" | "year";
  readonly text: string;
}
function daysIn(month: number, year: number | null): number {
  /* Without a year, February keeps its leap day, so nothing real is refused early. */
  const leap = year === null || year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  return [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 31;
}
function dayProblem(parts: Parts, year: number | null): Problem | null {
  const month = Number(parts.month);
  const day = Number(parts.day);
  const done = parts.day.length === 2 || parts.day !== "" && day > 3;
  if (!done) return null;
  if (day < 1) return {
    part: "day",
    text: "Enter a day from 1 to 31."
  };
  if (month < 1 || month > 12 || day <= daysIn(month, year)) return null;
  const name = MONTH_NAMES[month - 1] ?? "That month";
  const days = String(daysIn(month, year));
  return {
    part: "day",
    text: year !== null && month === 2 ? `${name} ${String(year)} has ${days} days.` : `${name} has ${days} days.`
  };
}
function futureProblem(parts: Parts, today: string): Problem | null {
  if (parts.year.length !== 4 || parts.month === "" || parts.day === "") return null;
  if (isoFromParts(parts.year, parts.month, parts.day, today) !== null) return null;
  const [thisYear = 0, thisMonth = 0] = today.split("-").map(Number);
  const part = ((): Problem["part"] => {
    if (Number(parts.year) > thisYear) return "year";
    return Number(parts.month) > thisMonth ? "month" : "day";
  })();
  return {
    part,
    text: `That date is in the future. Check the ${part}.`
  };
}

/**
 * The first thing wrong with what is typed, naming only the part that is wrong. A month or day that
 * cannot become valid is named as soon as it is complete, without waiting for the other parts.
 */
function problemOf(parts: Parts, today: string): Problem | null {
  const month = Number(parts.month);
  const year = parts.year.length === 4 ? Number(parts.year) : null;
  if (parts.month.length === 2 && (month < 1 || month > 12)) return {
    part: "month",
    text: "The month must be 1 to 12."
  };
  if (year !== null && year < FIRST_YEAR) return {
    part: "year",
    text: `The year must be ${String(FIRST_YEAR)} or later.`
  };
  return dayProblem(parts, year) ?? futureProblem(parts, today);
}
interface Parts {
  readonly year: string;
  readonly month: string;
  readonly day: string;
}
function partsOf(value: string): Parts {
  const [year = "", month = "", day = ""] = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.split("-") : [];
  return {
    year,
    month: month.replace(/^0/, ""),
    day: day.replace(/^0/, "")
  };
}
export interface DateInputProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly mode?: "date" | "birthDate";
  /** Names the group of fields in `birthDate` mode. */
  readonly label?: string;
  readonly id?: string | undefined;
  readonly disabled?: boolean;
  readonly "aria-describedby"?: string | undefined;
  readonly "aria-invalid"?: true | undefined;
  readonly "aria-required"?: true | undefined;
  readonly testId?: string;
}
export function DateInput({
  mode = "date",
  ...props
}: DateInputProps) {
  return mode === "birthDate" ? <BirthDate {...props} /> : <Input type="date" value={props.value} onChange={props.onChange} id={props.id} disabled={props.disabled ?? false} aria-describedby={props["aria-describedby"]} aria-invalid={props["aria-invalid"]} aria-required={props["aria-required"]} {...props.testId === undefined ? {} : {
    testId: props.testId
  }} />;
}
function BirthDate({
  value,
  onChange,
  label = "Date of birth",
  id,
  disabled = false,
  "aria-describedby": describedBy,
  "aria-invalid": invalidOuter,
  "aria-required": required,
  testId
}: DateInputProps) {
  const made = useId();
  const first = id ?? made;
  const errorId = `${first}-error`;
  const [parts, setParts] = useState(() => partsOf(value));
  const iso = isoFromParts(parts.year, parts.month, parts.day);
  const problem = problemOf(parts, new Date().toISOString().slice(0, 10));
  const wrong = problem !== null;

  /*
    A value that arrives from outside, a saved birthday loading or a form clearing after it was sent,
    replaces what the fields show. What the person types back out is the same value, so it matches
    and nothing is replaced while they type.
  */
  if (value !== (iso ?? "")) {
    const outside = partsOf(value);
    if (outside.year !== parts.year || outside.month !== parts.month || outside.day !== parts.day) setParts(outside);
  }
  const set = (next: Partial<Parts>) => {
    const merged = {
      ...parts,
      ...next
    };
    setParts(merged);
    onChange(isoFromParts(merged.year, merged.month, merged.day) ?? "");
  };
  const described = [describedBy, wrong ? errorId : undefined].filter(Boolean).join(" ");
  const part = (name: "month" | "day" | "year", caption: string, chars: number, fieldId: string) => <span {...stylex.props(styles.part, styles[name])}>
      <label htmlFor={fieldId} {...stylex.props(textStyle.caption, styles.caption)}>
        {caption}
      </label>
      <input id={fieldId} value={parts[name]} inputMode="numeric" autoComplete={{
      month: "bday-month",
      day: "bday-day",
      year: "bday-year"
    }[name]} maxLength={chars} disabled={disabled} aria-required={required} aria-invalid={(problem === null ? invalidOuter === true : problem.part === name) || undefined} aria-describedby={described === "" ? undefined : described} onChange={event => set({
      [name]: event.currentTarget.value.replaceAll(/\D/g, "")
    })} data-testid={testId === undefined ? undefined : `${testId}-${name}`} {...stylex.props(controlStyle.box, controlStyle.md, styles.number, (problem === null ? invalidOuter === true : problem.part === name) && controlStyle.invalid, problem?.part === name && styles.wrongPart, disabled && controlStyle.disabled, focusRing)} />
    </span>;
  return <span className="x78zum5 xdt5ytf xskm3c9 xeuugli">
      <span role="group" aria-label={label} className="x78zum5 x1cy8zhl x1173whf xeuugli">
        {part("month", "Month", 2, first)}
        {part("day", "Day", 2, `${first}-day`)}
        {part("year", "Year", 4, `${first}-year`)}
      </span>
      {problem !== null && <p id={errorId} role="alert" {...stylex.props(textStyle.label, styles.error)}>
          <WarningIcon />
          <span>{problem.text}</span>
        </p>}
    </span>;
}